import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Alert,
    CircularProgress
} from '@mui/material';
import { Upload, FileText, Trash2 } from 'lucide-react';
import { apiService } from '../../services/api';

const SafetyLetterManagement = () => {
    const [letters, setLetters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [uploadDialog, setUploadDialog] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadData, setUploadData] = useState({
        title: '',
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        file: null
    });

    useEffect(() => {
        fetchLetters();
    }, []);

    const fetchLetters = async () => {
        try {
            const data = await apiService.getAllSafetyLetters();
            setLetters(data);
        } catch (err) {
            setError('Failed to fetch safety letters');
            console.error('Error fetching letters:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file && file.type === 'application/pdf') {
            setUploadData(prev => ({ ...prev, file }));
            setError('');
        } else {
            setError('Please select a PDF file');
        }
    };

    const handleUpload = async () => {
        if (!uploadData.file || !uploadData.title.trim()) {
            setError('Please fill in all required fields');
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('title', uploadData.title);
            formData.append('month', uploadData.month);
            formData.append('year', uploadData.year);
            formData.append('file', uploadData.file);

            await apiService.uploadSafetyLetter(formData);
            setUploadDialog(false);
            setUploadData({
                title: '',
                month: new Date().getMonth() + 1,
                year: new Date().getFullYear(),
                file: null
            });
            await fetchLetters();
            setError('');
        } catch (err) {
            console.error('Upload error:', err);
            setError(err.message || 'Failed to upload safety letter');
        } finally {
            setUploading(false);
        }
    };

    const handleView = async (letter) => {
        try {
            // Use the file_url directly if it exists
            if (letter.file_url) {
                window.open(letter.file_url, '_blank');
            } else {
                // If file_url is not available, inform the user
                setError('Letter URL is not available');
            }
        } catch (err) {
            setError('Failed to open safety letter');
            console.error('Error viewing letter:', err);
        }
    };

    const handleDelete = async (letterId) => {
        if (window.confirm('Are you sure you want to delete this safety letter?')) {
            try {
                await apiService.deleteSafetyLetter(letterId);
                await fetchLetters();
            } catch (err) {
                setError('Failed to delete safety letter');
                console.error('Error deleting letter:', err);
            }
        }
    };

    const handleClose = () => {
        if (!uploading) {
            setUploadDialog(false);
            setError('');
            setUploadData({
                title: '',
                month: new Date().getMonth() + 1,
                year: new Date().getFullYear(),
                file: null
            });
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h5">Safety Letters Management</Typography>
                <Button
                    variant="contained"
                    startIcon={<Upload />}
                    onClick={() => setUploadDialog(true)}
                >
                    Upload New Letter
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Title</TableCell>
                            <TableCell>Month/Year</TableCell>
                            <TableCell>Signatures</TableCell>
                            <TableCell>Upload Date</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {letters.length > 0 ? (
                            letters.map((letter) => (
                                <TableRow key={letter.id}>
                                    <TableCell>{letter.title}</TableCell>
                                    <TableCell>
                                        {new Date(letter.year, letter.month - 1).toLocaleString('default', {
                                            month: 'long',
                                            year: 'numeric'
                                        })}
                                    </TableCell>
                                    <TableCell>
                                        {letter.signature_count || 0} signatures
                                    </TableCell>
                                    <TableCell>
                                        {new Date(letter.created_at).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                            <Button
                                                size="small"
                                                startIcon={<FileText size={16} />}
                                                onClick={() => handleView(letter)}
                                            >
                                                View
                                            </Button>
                                            <Button
                                                size="small"
                                                color="error"
                                                startIcon={<Trash2 size={16} />}
                                                onClick={() => handleDelete(letter.id)}
                                            >
                                                Delete
                                            </Button>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    No safety letters found
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog
                open={uploadDialog}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Upload Safety Letter</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Title"
                        value={uploadData.title}
                        onChange={(e) => setUploadData(prev => ({ ...prev, title: e.target.value }))}
                        margin="normal"
                    />
                    <TextField
                        select
                        fullWidth
                        label="Month"
                        value={uploadData.month}
                        onChange={(e) => setUploadData(prev => ({ ...prev, month: e.target.value }))}
                        margin="normal"
                    >
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                            <MenuItem key={month} value={month}>
                                {new Date(2000, month - 1).toLocaleString('default', { month: 'long' })}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        fullWidth
                        label="Year"
                        value={uploadData.year}
                        onChange={(e) => setUploadData(prev => ({ ...prev, year: e.target.value }))}
                        margin="normal"
                    >
                        {Array.from({ length: 3 }, (_, i) => new Date().getFullYear() + i).map((year) => (
                            <MenuItem key={year} value={year}>
                                {year}
                            </MenuItem>
                        ))}
                    </TextField>
                    <input
                        accept="application/pdf"
                        style={{ display: 'none' }}
                        id="raised-button-file"
                        type="file"
                        onChange={handleFileSelect}
                    />
                    <label htmlFor="raised-button-file">
                        <Button
                            variant="outlined"
                            component="span"
                            fullWidth
                            sx={{ mt: 2 }}
                            disabled={uploading}
                        >
                            {uploadData.file ? uploadData.file.name : 'Select PDF File'}
                        </Button>
                    </label>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={uploading}>Cancel</Button>
                    <Button
                        onClick={handleUpload}
                        disabled={uploading || !uploadData.title || !uploadData.file}
                        variant="contained"
                    >
                        {uploading ? <CircularProgress size={24} /> : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SafetyLetterManagement;